import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import CartCheckoutDialog from "../components/CartCheckoutDialog";
import { api } from "../lib/api";

const Cart = () => {
  const { items, remove, inc, dec, subtotal, count } = useCart();
  const [checkoutOpen, setCheckoutOpen] = React.useState(false);
  const [inventory, setInventory] = React.useState(null);

  React.useEffect(() => {
    api.get("/inventory")
      .then((r) => setInventory(r.data))
      .catch((e) => console.error("Cart: /inventory fetch failed", e));
  }, []);

  const soldOut = !!inventory?.sold_out;
  const remaining = inventory?.remaining ?? Infinity;
  const overCap = count > remaining;

  const openCheckout = async () => {
    if (!items.length) return;
    // Race guard: re-fetch inventory RIGHT before opening the checkout dialog
    // so a customer sitting on /cart through a sell-out sees the inline state
    // instead of hitting an HTTP 409 mid-flow.
    try {
      const { data } = await api.get("/inventory");
      setInventory(data);
      if (data.sold_out || (typeof data.remaining === "number" && data.remaining < count)) {
        return;
      }
    } catch (e) {
      // Network hiccup — proceed; backend will 409 if truly sold out
      console.error("Cart.openCheckout: inventory re-check failed", e);
    }
    setCheckoutOpen(true);
  };

  return (
    <div className="w-full">
      <section className="w-full" style={{ backgroundColor: "#FDE6EE" }}>
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-3">
            Your bag
          </div>
          <h1 className="font-serif-display font-black text-5xl md:text-6xl leading-[0.95] tracking-tight">
            Cart{count > 0 && (<span className="text-[#FF1493]"> · {count}</span>)}
          </h1>
        </div>
      </section>

      <section className="w-full bg-white">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-14 md:py-16">
          {!items.length ? (
            <div data-testid="cart-empty" className="text-center py-10">
              <p className="font-serif-display italic text-2xl text-black/70 mb-6">
                Your bag is empty.
              </p>
              <Link
                to="/"
                data-testid="cart-continue-shopping"
                className="inline-block bg-black text-white overline text-[11px] tracking-[0.22em] px-6 py-3 hover:bg-[#FF1493] transition-colors"
              >
                Start bottling
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-[1fr_320px] gap-10">
              <div className="flex flex-col gap-4" data-testid="cart-items">
                {items.map((it) => (
                  <div
                    key={it.id}
                    data-testid={`cart-item-${it.id}`}
                    className="flex items-start justify-between border border-black p-4 md:p-5 gap-4"
                  >
                    <div className="min-w-0">
                      <div className="overline text-[10px] text-[#FF1493] tracking-[0.22em] mb-1">
                        {it.bottle_size} · {(it.scent_symbols || []).length} notes
                      </div>
                      <div className="font-serif-display font-black text-xl md:text-2xl leading-tight truncate">
                        {it.blend_name || "Your custom blend"}
                      </div>
                      {(it.scent_symbols || []).length > 0 && (
                        <div className="mt-1 text-xs text-black/60">
                          {(it.scent_symbols || []).join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="font-serif-display font-black text-xl">
                        ${(Number(it.price) * (it.qty || 1)).toFixed(2)}
                      </div>
                      <div
                        data-testid={`cart-qty-${it.id}`}
                        className="flex items-center border border-black"
                      >
                        <button
                          data-testid={`cart-dec-${it.id}`}
                          onClick={() => dec(it.id)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white transition-colors text-base leading-none"
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-serif-display text-sm">
                          {it.qty || 1}
                        </span>
                        <button
                          data-testid={`cart-inc-${it.id}`}
                          onClick={() => inc(it.id)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white transition-colors text-base leading-none"
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.id}`}
                        onClick={() => remove(it.id)}
                        className="overline text-[10px] tracking-[0.22em] text-black/60 hover:text-[#FF1493] transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <aside
                data-testid="cart-summary"
                className="border border-black p-5 md:p-6 self-start h-fit"
                style={{ backgroundColor: "#FFF1F5" }}
              >
                <div className="overline text-[10px] tracking-[0.3em] text-black/70 mb-1">
                  Summary
                </div>
                <div className="flex items-baseline justify-between mt-3 mb-4">
                  <span className="font-sans-display text-sm text-black/80">
                    Subtotal
                  </span>
                  <span
                    data-testid="cart-subtotal"
                    className="font-serif-display font-black text-3xl text-black"
                  >
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <p className="text-[11px] text-black/60 italic mb-4">
                  Shipping + any promo applied at payment.
                </p>
                {(soldOut || overCap) && (
                  <p
                    data-testid="cart-sold-out-note"
                    className="text-[11px] text-[#FF1493] italic mb-3 leading-snug"
                  >
                    {soldOut
                      ? "Temporarily sold out — sign up for the newsletter to catch the next drop."
                      : `Only ${remaining} bottle${remaining === 1 ? "" : "s"} left in this batch — reduce your cart to continue.`}
                  </p>
                )}
                <button
                  data-testid="cart-proceed-btn"
                  onClick={openCheckout}
                  disabled={soldOut || overCap}
                  className={
                    soldOut || overCap
                      ? "w-full bg-black/30 text-white font-sans-display uppercase tracking-[0.18em] text-sm py-4 px-6 rounded-full border border-black cursor-not-allowed"
                      : "w-full bg-[#FF1493] text-white font-sans-display uppercase tracking-[0.18em] text-sm py-4 px-6 rounded-full hover:bg-black transition-colors border border-black"
                  }
                >
                  {soldOut
                    ? "Temporarily Sold Out"
                    : overCap
                    ? "Adjust cart to continue"
                    : "Proceed to payment"}
                </button>
                <Link
                  to="/"
                  className="block text-center mt-3 overline text-[10px] tracking-[0.22em] text-black/60 hover:text-black"
                >
                  ← Keep shopping
                </Link>
              </aside>
            </div>
          )}
        </div>
      </section>

      <CartCheckoutDialog
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        cartItems={items}
        subtotal={subtotal}
      />
    </div>
  );
};

export default Cart;
